/**
 * Possible Values Input Component
 * Row-based list with add/edit/delete using modal for better visibility
 */

import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AllowedValueEditModal } from './AllowedValueEditModal';
import { FIELD_TOOLTIPS } from '../constants/fieldTooltips';

interface PossibleValuesInputProps {
  dataType: string;
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
  label?: string;
  tooltipKey?: string;
}

export const AllowedValuesInput: React.FC<PossibleValuesInputProps> = ({
  dataType,
  value,
  onChange,
  disabled = false,
  label,
  tooltipKey,
}) => {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [modalValue, setModalValue] = useState<string>('');
  const [editIndex, setEditIndex] = useState<number | null>(null);

  // Convert value to array of strings for display
  const getDisplayValues = (): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map(v => typeof v === 'string' ? v : JSON.stringify(v));
    }
    return [typeof value === 'string' ? value : JSON.stringify(value)];
  };

  // Convert string to appropriate type
  const parseValue = (str: string): any => {
    switch (dataType) {
      case 'BOOLEAN':
        return str === 'true';
      case 'INTEGER':
        return parseInt(str, 10);
      case 'FLOAT':
        return parseFloat(str);
      case 'JSON':
        try {
          return JSON.parse(str);
        } catch {
          return str;
        }
      default:
        return str;
    }
  };

  const displayValues = getDisplayValues();

  // Open modal for adding
  const handleOpenAddModal = () => {
    setModalMode('add');
    setModalValue('');
    setEditIndex(null);
    setModalOpen(true);
  };

  // Open modal for editing
  const handleOpenEditModal = (index: number) => {
    setModalMode('edit');
    setModalValue(displayValues[index]);
    setEditIndex(index);
    setModalOpen(true);
  };

  // Save from modal
  const handleModalSave = (newVal: string) => {
    if (modalMode === 'add') {
      // Add new value
      const updated = [...displayValues, newVal];
      onChange(updated.map(parseValue));
    } else if (modalMode === 'edit' && editIndex !== null) {
      // Edit existing value
      const updated = [...displayValues];
      updated[editIndex] = newVal;
      onChange(updated.map(parseValue));
    }
  };

  // Delete value
  const handleDelete = (index: number) => {
    const updated = displayValues.filter((_, i) => i !== index);
    onChange(updated.length > 0 ? updated.map(parseValue) : null);
  };

  return (
    <Box>
      {/* Label with inline Add button */}
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography variant="body2" component="label" sx={{ fontWeight: 500, textAlign: 'left' }}>
            {label}
          </Typography>
          {tooltipKey && FIELD_TOOLTIPS[tooltipKey] && (
            <Tooltip title={FIELD_TOOLTIPS[tooltipKey]} arrow placement="top">
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          )}
          <IconButton
            size="small"
            onClick={handleOpenAddModal}
            disabled={disabled}
            color="primary"
            sx={{
              p: 0.5,
              ml: 0.5,
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Values Table - Compact with max 4 rows visible */}
      {displayValues.length > 0 && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <TableContainer
            sx={{
              maxHeight: 200, // Show ~4 rows max, then scroll
              overflow: 'auto',
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'primary.main',
                      color: 'white',
                      py: 1,
                      fontSize: '0.875rem',
                      borderBottom: '2px solid',
                      borderBottomColor: 'primary.dark',
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'primary.main',
                      color: 'white',
                      py: 1,
                      fontSize: '0.875rem',
                      borderBottom: '2px solid',
                      borderBottomColor: 'primary.dark',
                    }}
                  >
                    Value
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'primary.main',
                      color: 'white',
                      width: 120,
                      py: 1,
                      fontSize: '0.875rem',
                      borderBottom: '2px solid',
                      borderBottomColor: 'primary.dark',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayValues.map((val, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                      bgcolor: index % 2 === 0 ? 'background.paper' : 'grey.50',
                    }}
                  >
                    <TableCell sx={{ width: 50, py: 1.25, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.25, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: dataType === 'JSON' ? 'monospace' : 'inherit',
                          fontWeight: 500,
                          color: 'text.primary',
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {val}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.25, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditModal(index)}
                          disabled={disabled}
                          sx={{
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'primary.lighter' },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(index)}
                          disabled={disabled}
                          sx={{
                            color: 'error.main',
                            '&:hover': { bgcolor: 'error.lighter' },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {displayValues.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontStyle: 'italic',
            py: 1.5,
            px: 2,
            textAlign: 'center',
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'grey.300',
          }}
        >
          No values added yet. Click "+" to add values.
        </Typography>
      )}

      {/* Edit Modal */}
      <AllowedValueEditModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleModalSave}
        initialValue={modalValue}
        dataType={dataType}
        mode={modalMode}
      />
    </Box>
  );
};

